import { Flex, Icon, IconButton, Menu, MenuButton, Image, MenuItem, MenuList, Text } from '@chakra-ui/react';
import { createColumnHelper } from '@tanstack/react-table';
import { formatDateToDateTimeDisplay } from '@/utils/formatDateToDateTimeDisplay';
import { CustomTable } from '@/components/CustomTable/CustomTable';
import { ChakraFeatherIcon } from '@/components/ChakraFeatherIcon';
import { EllipsisVerticalIcon } from '@heroicons/react/24/outline';
import { FaStar } from 'react-icons/fa';
import { Club } from '@/model';
import { useTranslation } from 'react-i18next';

export interface TeamsTableProps {
  data: Club[];
  isLoading: boolean;
  pageNumber: number;
  pageSize: number;
  totalPages: number;
  totalItems: number;
  sorting?: { field: string; desc: boolean };
  onSortingChange?: (field: string, desc: boolean) => void;
  onPaginationChange: (page: number, pageSize: number) => void;
  onEditClick: (item: Club) => void;
  onDeleteClick: (item: Club) => void;
}

export const TeamsTable = ({
  data,
  isLoading,
  pageNumber,
  pageSize,
  totalPages,
  sorting,
  totalItems,
  onSortingChange,
  onPaginationChange,
  onEditClick,
  onDeleteClick,
}: TeamsTableProps) => {
  const { t } = useTranslation();
  const columnHelper = createColumnHelper<{
    created_at: string;
    logo_path: string;
    name: string;
    acronym: string;
    favorite?: boolean;
  }>();

  const columns = [
    columnHelper.accessor('created_at', {
      cell: props => formatDateToDateTimeDisplay(props.getValue()),
      header: t('pages.application_teams.components.teams_table.teams_table_created_at_header'),
    }),
    columnHelper.accessor('logo_path', {
      cell: props => <Image src={props.getValue()} alt='' w='24px' h='24px' objectFit='contain' />,
      header: t('pages.application_teams.components.teams_table.teams_table_logo_header'),
    }),
    columnHelper.accessor('name', {
      cell: props => (
        <Flex alignItems='center' gap={2}>
          <Text>{props.getValue()}</Text>
          {props.row.original.favorite && <Icon as={FaStar} color='brand.500' w='18px' h='18px' />}
        </Flex>
      ),
      header: t('pages.application_teams.components.teams_table.teams_table_name_header'),
    }),
    columnHelper.accessor('acronym', {
      cell: props => props.getValue(),
      header: t('pages.application_teams.components.teams_table.teams_table_acronym_header'),
    }),
    columnHelper.display({
      id: 'actions',
      header: t('pages.application_teams.components.teams_table.teams_table_actions_header'),
      cell: props => (
        <Flex justifyContent={'end'}>
          <Menu>
            <MenuButton
              textColor='text.table'
              as={IconButton}
              variant='ghost'
              size='sm'
              icon={<Icon as={EllipsisVerticalIcon} />}
              aria-label={t('pages.application_teams.components.teams_table.teams_table_show_more_actions')}
            />
            <MenuList textColor='white'>
              <MenuItem
                onClick={() => onEditClick?.(props.row.original as Club)}
                icon={
                  <Text>
                    <ChakraFeatherIcon name='eye' />
                  </Text>
                }
              >
                <Text>{t('pages.application_teams.components.teams_table.teams_table_view_details')}</Text>
              </MenuItem>
              <MenuItem
                onClick={() => onDeleteClick?.(props.row.original as Club)}
                icon={
                  <Text>
                    <ChakraFeatherIcon name='trash-2' color='red.500' w='18px' />
                  </Text>
                }
              >
                <Text>{t('pages.application_teams.components.teams_table.teams_table_delete')}</Text>
              </MenuItem>
            </MenuList>
          </Menu>
        </Flex>
      ),
      enableSorting: false,
      meta: {
        align: 'right',
      },
    }),
  ];

  return (
    <CustomTable
      data={data}
      isLoading={isLoading}
      pageNumber={pageNumber}
      pageSize={pageSize}
      totalPages={totalPages}
      totalItems={totalItems}
      columns={columns}
      onPaginationChange={onPaginationChange}
      sorting={sorting}
      onSortingChange={onSortingChange}
    />
  );
};
